import { notifier } from "../../core/notifier/notifier";
import { ScrollLock } from "../../core/scroll-lock";
import { spinner } from "../../util/spinner/spinner";
import { ModalNestedPosition } from "../../util/modal-nested-position";
import {
    UsersAdminApi,
    type UsersAdminCreateAccountResponse,
    type UsersAdminDeleteResponse,
    type UsersAdminLoginHistoryResponse,
    type UsersAdminUpdateResponse,
} from "./users-admin-api";
import { UsersAdminUtils, type UsersAdminLoginHistoryItem, type UsersAdminUser } from "./users-admin-utils";

interface UsersAdminFormLike {
    getRoot(): HTMLElement;
    getUsersData(): UsersAdminUser[];
    getCreateModal(): HTMLElement | null;
    getCreateForm(): HTMLFormElement | null;
    getCreateEmailInput(): HTMLInputElement | null;
    getCreateNicknameInput(): HTMLInputElement | null;
    getCreateResultEl(): HTMLElement | null;
    getCreateResultPasswordInput(): HTMLInputElement | null;
    getCreateResultEmailInput(): HTMLInputElement | null;
    getCreateResultNicknameInput(): HTMLInputElement | null;
    getCreateSubmitButton(): HTMLButtonElement | null;
    getSearchEl(): Element | null;
    getRoleFilterEl(): Element | null;
    getUserStatusFilterEl(): Element | null;
    getHistoryModal(): HTMLElement | null;
    getHistoryButton(root: EventTarget | Element | null | undefined): HTMLButtonElement | null;
    getHistoryOverlay(): HTMLElement | null;
    getEditButton(root: EventTarget | Element | null | undefined): HTMLButtonElement | null;
    getSaveButton(root: EventTarget | Element | null | undefined): HTMLButtonElement | null;
    getDeleteButton(root: EventTarget | Element | null | undefined): HTMLButtonElement | null;
    getHistoryCloseButtons(): HTMLElement[];
    getRow(root: EventTarget | Element | null | undefined): HTMLElement | null;
    getRoleSelect(root: ParentNode): HTMLSelectElement | null;
    getUserStatusSelect(root: ParentNode): HTMLSelectElement | null;
    getUserNoteInput(root: ParentNode): HTMLTextAreaElement | null;
}

interface UsersAdminRenderLike {
    renderMeta(visibleCount: number, totalCount: number): void;
    renderList(users: UsersAdminUser[]): void;
    renderHistoryTitle(displayName: string): void;
    renderHistoryLoading(): void;
    renderHistoryError(): void;
    renderHistoryList(items: UsersAdminLoginHistoryItem[]): void;
}

export class UsersAdminHandler {
    public form: UsersAdminFormLike;

    public render: UsersAdminRenderLike;

    public users: UsersAdminUser[];

    public usersById: Map<number, UsersAdminUser>;

    public activeHistoryUserId: number | null;

    public activeHistoryItems: UsersAdminLoginHistoryItem[];

    public totalUserCount: number;

    public constructor(form: UsersAdminFormLike, render: UsersAdminRenderLike) {
        this.form = form;
        this.render = render;
        this.users = [];
        this.usersById = new Map();
        this.activeHistoryUserId = null;
        this.activeHistoryItems = [];
        this.totalUserCount = 0;
    }

    private static getUserIdFromAttribute(element: Element): number | null {
        return UsersAdminUtils.toUserId(element.getAttribute("data-user-id"));
    }

    public bootstrap(): void {
        const root = this.form.getRoot();
        this.users = this.form.getUsersData();
        this.totalUserCount = Math.max(
            this.users.length,
            Number.parseInt(root.dataset.totalCount || "", 10) || this.users.length,
        );
        this.usersById = new Map(
            this.users
                .map((user) => [UsersAdminUtils.toUserId(user.user_id), user] as const)
                .filter((entry): entry is readonly [number, UsersAdminUser] => entry[0] !== null),
        );
        this.applyFilters();
    }

    public openCreateModal(): void {
        const modal = this.form.getCreateModal();
        if (!(modal instanceof HTMLElement)) {
            return;
        }

        this.resetCreateModal();
        ModalNestedPosition.openModal({ modalEl: modal, displayValue: "block" });
        ScrollLock.lock();
    }

    public closeCreateModal(): void {
        const modal = this.form.getCreateModal();
        if (!(modal instanceof HTMLElement)) {
            return;
        }

        ModalNestedPosition.closeModal({ modalEl: modal });
        ScrollLock.unlock();
        this.resetCreateModal();
    }

    private resetCreateModal(): void {
        const formEl = this.form.getCreateForm();
        const resultEl = this.form.getCreateResultEl();
        const passwordEl = this.form.getCreateResultPasswordInput();
        const emailEl = this.form.getCreateResultEmailInput();
        const nicknameEl = this.form.getCreateResultNicknameInput();

        if (formEl instanceof HTMLFormElement) {
            formEl.reset();
        }

        if (resultEl instanceof HTMLElement) {
            resultEl.hidden = true;
        }

        if (passwordEl instanceof HTMLInputElement) {
            passwordEl.value = "";
        }

        if (emailEl instanceof HTMLInputElement) {
            emailEl.value = "";
        }

        if (nicknameEl instanceof HTMLInputElement) {
            nicknameEl.value = "";
        }
    }

    public applyFilters(): void {
        this.render.renderMeta(this.users.length, this.totalUserCount);
        this.render.renderList(this.users);
    }

    public handleListClick(event: MouseEvent): void {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }

        const saveBtn = this.form.getSaveButton(target);
        if (saveBtn instanceof HTMLButtonElement) {
            event.preventDefault();
            const userId = UsersAdminHandler.getUserIdFromAttribute(saveBtn);
            if (userId === null) return;
            void this.saveRow(userId, saveBtn);
            return;
        }

        const historyBtn = this.form.getHistoryButton(target);
        if (historyBtn instanceof HTMLButtonElement) {
            event.preventDefault();
            const userId = UsersAdminHandler.getUserIdFromAttribute(historyBtn);
            if (userId === null) return;
            void this.openHistoryModal(userId, historyBtn);
            return;
        }

        const deleteBtn = this.form.getDeleteButton(target);
        if (deleteBtn instanceof HTMLButtonElement) {
            event.preventDefault();
            const userId = UsersAdminHandler.getUserIdFromAttribute(deleteBtn);
            if (userId === null) return;
            void this.deleteRow(userId, deleteBtn);
            return;
        }

        const editBtn = this.form.getEditButton(target);
        if (editBtn instanceof HTMLButtonElement) {
            event.preventDefault();
            const userId = UsersAdminHandler.getUserIdFromAttribute(editBtn);
            if (userId === null) return;
            this.toggleEditRow(userId, editBtn);
        }
    }

    public closeHistoryModal(): void {
        const modal = this.form.getHistoryModal();
        if (!(modal instanceof HTMLElement)) {
            return;
        }

        ModalNestedPosition.closeModal({ modalEl: modal });
        ScrollLock.unlock();
        this.activeHistoryUserId = null;
        this.activeHistoryItems = [];
    }

    public async openHistoryModal(userId: number, button: HTMLButtonElement): Promise<void> {
        const user = this.usersById.get(userId);
        const modal = this.form.getHistoryModal();
        if (!user || !(modal instanceof HTMLElement)) {
            return;
        }

        const displayName =
            UsersAdminUtils.toText(user.display_name) ||
            UsersAdminUtils.toText(user.nick_name) ||
            UsersAdminUtils.nameNotRegisteredLabel();

        this.activeHistoryUserId = userId;
        this.activeHistoryItems = [];
        this.render.renderHistoryTitle(displayName);
        this.render.renderHistoryLoading();
        ModalNestedPosition.openModal({ modalEl: modal, displayValue: "block" });
        ScrollLock.lock();

        button.disabled = true;

        try {
            const response = await UsersAdminApi.getLoginHistoryByUser(userId);
            const result = (await response.json().catch(() => ({}))) as UsersAdminLoginHistoryResponse;

            if (!response.ok || result.success !== 1) {
                this.render.renderHistoryError();
                return;
            }

            this.activeHistoryItems = UsersAdminUtils.toLoginHistoryArray(result.data ?? []);
            this.render.renderHistoryList(this.activeHistoryItems);
        } catch {
            this.render.renderHistoryError();
        } finally {
            button.disabled = false;
        }
    }

    public handleListChange(event: Event): void {
        const target = event.target;
        if (!(target instanceof HTMLSelectElement)) {
            return;
        }

        const row = this.form.getRow(target);
        if (row instanceof HTMLElement) {
            row.dataset.dirty = "1";
        }
    }

    public async submitCreateForm(event: SubmitEvent): Promise<void> {
        event.preventDefault();

        const emailEl = this.form.getCreateEmailInput();
        const nicknameEl = this.form.getCreateNicknameInput();
        const submitBtn = this.form.getCreateSubmitButton();
        const resultEl = this.form.getCreateResultEl();
        const resultPasswordEl = this.form.getCreateResultPasswordInput();
        const resultEmailEl = this.form.getCreateResultEmailInput();
        const resultNicknameEl = this.form.getCreateResultNicknameInput();

        const email = emailEl instanceof HTMLInputElement ? emailEl.value.trim() : "";
        const nickname = nicknameEl instanceof HTMLInputElement ? nicknameEl.value.trim() : "";

        if (email === "") {
            notifier.error("users_admin_email_required");
            return;
        }

        if (nickname === "") {
            notifier.error("users_admin_nickname_required");
            return;
        }

        try {
            if (submitBtn instanceof HTMLButtonElement) {
                spinner.showButtonLoader(submitBtn, { translateKey: "spinner_proccesing" });
            }

            const response = await UsersAdminApi.createAdminAccount({ email, nickname });
            const result = (await response.json().catch(() => ({}))) as UsersAdminCreateAccountResponse;

            if (!response.ok || result.success !== 1 || typeof result.generated_password !== "string") {
                const message =
                    typeof result.message === "string" && result.message ? result.message : "users_admin_create_failed";
                notifier.error(message);
                return;
            }

            const createdUser = (result.user || {}) as UsersAdminUser;
            const userId = UsersAdminUtils.toUserId(createdUser.user_id);
            if (userId !== null) {
                this.users = [...this.users, createdUser];
                this.usersById.set(userId, createdUser);
                this.totalUserCount += 1;
                this.applyFilters();
            }

            if (resultEl instanceof HTMLElement) {
                resultEl.hidden = false;
            }
            if (resultPasswordEl instanceof HTMLInputElement) {
                resultPasswordEl.value = result.generated_password;
            }
            if (resultEmailEl instanceof HTMLInputElement) {
                resultEmailEl.value = email;
            }
            if (resultNicknameEl instanceof HTMLInputElement) {
                resultNicknameEl.value = nickname;
            }

            notifier.success("users_admin_create_success");
        } catch {
            notifier.error("users_admin_create_failed");
        } finally {
            if (submitBtn instanceof HTMLButtonElement) {
                spinner.hideButtonLoader(submitBtn);
            }
        }
    }

    public toggleEditRow(userId: number, button: HTMLButtonElement): void {
        const user = this.usersById.get(userId);
        if (!user) {
            return;
        }

        const row = this.form.getRow(button);
        if (!(row instanceof HTMLElement)) {
            return;
        }

        const isEditing = row.dataset.editing === "1";
        if (isEditing) {
            this.resetRowInputs(row, user);
            row.dataset.editing = "0";
            row.dataset.dirty = "0";
            return;
        }

        this.resetRowInputs(row, user);
        row.dataset.editing = "1";
    }

    private resetRowInputs(row: HTMLElement, user: UsersAdminUser): void {
        const roleSelect = this.form.getRoleSelect(row);
        const userStatusSelect = this.form.getUserStatusSelect(row);
        const userNoteInput = this.form.getUserNoteInput(row);

        if (roleSelect instanceof HTMLSelectElement) {
            roleSelect.value = UsersAdminUtils.normalizeRole(user.role) || "user";
        }

        if (userStatusSelect instanceof HTMLSelectElement) {
            userStatusSelect.value = UsersAdminUtils.normalizeStatus(user.user_status) || "active";
        }

        if (userNoteInput instanceof HTMLTextAreaElement) {
            userNoteInput.value = UsersAdminUtils.toText(user.user_note);
        }
    }

    public async saveRow(userId: number, button: HTMLButtonElement): Promise<void> {
        const user = this.usersById.get(userId);
        if (!user) {
            return;
        }

        const row = this.form.getRow(button);
        if (!(row instanceof HTMLElement)) {
            return;
        }

        const roleSelect = this.form.getRoleSelect(row);
        const userStatusSelect = this.form.getUserStatusSelect(row);
        if (!(roleSelect instanceof HTMLSelectElement) || !(userStatusSelect instanceof HTMLSelectElement)) {
            return;
        }
        const userNoteInput = this.form.getUserNoteInput(row);

        const nextRole = UsersAdminUtils.normalizeRole(roleSelect.value);
        const nextUserStatus = UsersAdminUtils.normalizeStatus(userStatusSelect.value);
        const nextUserNote =
            userNoteInput instanceof HTMLTextAreaElement
                ? userNoteInput.value
                : UsersAdminUtils.toText(user.user_note);
        const currentRole = UsersAdminUtils.normalizeRole(user.role);
        const currentUserStatus = UsersAdminUtils.normalizeStatus(user.user_status);
        const currentUserNote = UsersAdminUtils.toText(user.user_note);

        if (nextRole === currentRole && nextUserStatus === currentUserStatus && nextUserNote === currentUserNote) {
            notifier.success("users_admin_no_changes_to_save");
            row.dataset.dirty = "0";
            return;
        }

        button.disabled = true;

        try {
            const payload = UsersAdminUtils.buildUpdatePayload(user, nextRole, nextUserStatus, nextUserNote);
            const response = await UsersAdminApi.updateUser(userId, payload);
            const result = (await response.json().catch(() => ({}))) as UsersAdminUpdateResponse;

            if (!response.ok || result.success !== 1) {
                const message =
                    typeof result.message === "string" && result.message
                        ? result.message
                        : "master_data_user_update_failed";
                notifier.error(message);
                return;
            }

            user.role = nextRole;
            user.user_status = nextUserStatus;
            user.user_note = nextUserNote;
            row.dataset.dirty = "0";
            row.dataset.editing = "0";
            notifier.success("master_data_user_updated");
            this.applyFilters();
        } catch (error) {
            notifier.error(error instanceof Error && error.message ? error.message : "master_data_user_update_failed");
        } finally {
            button.disabled = false;
        }
    }

    public async deleteRow(userId: number, button: HTMLButtonElement): Promise<void> {
        const user = this.usersById.get(userId);
        if (!user) {
            return;
        }

        const confirmed = await notifier.confirm("master_data_user_delete_confirm", {
            translateKey: "master_data_user_delete_confirm",
            okTranslateKey: "delete",
            cancelTranslateKey: "cancel",
        });
        if (!confirmed) {
            return;
        }

        button.disabled = true;

        try {
            const response = await UsersAdminApi.deleteUser(userId);
            const result = (await response.json().catch(() => ({}))) as UsersAdminDeleteResponse;

            if (!response.ok || result.success !== 1) {
                const message =
                    typeof result.message === "string" && result.message
                        ? result.message
                        : "master_data_user_delete_failed";
                notifier.error(message);
                return;
            }

            this.users = this.users.filter((item) => UsersAdminUtils.toUserId(item.user_id) !== userId);
            this.usersById.delete(userId);
            this.totalUserCount = Math.max(0, this.totalUserCount - 1);
            notifier.success("master_data_user_deleted");
            this.applyFilters();
        } catch {
            notifier.error("master_data_user_delete_failed");
        } finally {
            button.disabled = false;
        }
    }
}
